// client/src/components/Auth/PermissionsContext.jsx
import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/components/hooks/useAuth';
import { hasPermission, hasAnyPermission, hasAllPermissions, PERMISSIONS } from '@/utils/permissions';

// Create context
const PermissionsContext = createContext({
  permissions: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  check: {
    canReadAdmins: false,
    canWriteAdmins: false,
    canDeleteAdmins: false,
    canReadProperties: false,
    canWriteProperties: false,
    canDeleteProperties: false,
    canReadBuyers: false,
    canWriteBuyers: false,
    canDeleteBuyers: false,
    canReadOffers: false,
    canWriteOffers: false,
    canDeleteOffers: false,
    canReadQualifications: false,
    canWriteQualifications: false,
    canReadEmailLists: false,
    canWriteEmailLists: false,
    canDeleteEmailLists: false,
    canAccessAdmin: false,
    canAccessTraffic: false,
    canAccessSettings: false,
    canExposeSettings: false,
    canEmailSettings: false,
  },
});

// Provider component
export const PermissionsProvider = ({ children }) => {
  const { userPermissions } = useAuth();
  const permissions = useMemo(
    () => (Array.isArray(userPermissions) ? userPermissions : []),
    [userPermissions]
  );
  
  // Create a memo with permission check methods
  const value = useMemo(() => {
    // Create common permission check functions
    const permissionHelpers = {
      permissions,
      hasPermission: (permission) => hasPermission(permissions, permission),
      hasAnyPermission: (permissionList) => hasAnyPermission(permissions, permissionList),
      hasAllPermissions: (permissionList) => hasAllPermissions(permissions, permissionList),
    };
    
    // Create boolean flags for common permission checks
    const check = {
      canObscureProperties: hasPermission(permissions, PERMISSIONS.OBSCURE_PROPERTIES),
      canReadAdmins: hasPermission(permissions, PERMISSIONS.READ_ADMINS),
      canWriteAdmins: hasPermission(permissions, PERMISSIONS.WRITE_ADMINS),
      canDeleteAdmins: hasPermission(permissions, PERMISSIONS.DELETE_ADMINS),
      canReadProperties: hasPermission(permissions, PERMISSIONS.READ_PROPERTIES),
      canWriteProperties: hasPermission(permissions, PERMISSIONS.WRITE_PROPERTIES),
      canDeleteProperties: hasPermission(permissions, PERMISSIONS.DELETE_PROPERTIES),
      canReadBuyers: hasPermission(permissions, PERMISSIONS.READ_BUYERS),
      canWriteBuyers: hasPermission(permissions, PERMISSIONS.WRITE_BUYERS),
      canDeleteBuyers: hasPermission(permissions, PERMISSIONS.DELETE_BUYERS),
      canReadOffers: hasPermission(permissions, PERMISSIONS.READ_OFFERS),
      canWriteOffers: hasPermission(permissions, PERMISSIONS.WRITE_OFFERS),
      canDeleteOffers: hasPermission(permissions, PERMISSIONS.DELETE_OFFERS),
      canReadQualifications: hasPermission(permissions, PERMISSIONS.READ_QUALIFICATIONS),
      canWriteQualifications: hasPermission(permissions, PERMISSIONS.WRITE_QUALIFICATIONS),
      canReadEmailLists: hasPermission(permissions, PERMISSIONS.READ_EMAIL_LISTS),
      canWriteEmailLists: hasPermission(permissions, PERMISSIONS.WRITE_EMAIL_LISTS),
      canDeleteEmailLists: hasPermission(permissions, PERMISSIONS.DELETE_EMAIL_LISTS),
      canAccessAdmin: hasPermission(permissions, PERMISSIONS.ACCESS_ADMIN),
      canAccessTraffic: hasPermission(permissions, PERMISSIONS.ACCESS_TRAFFIC),
      canAccessSettings: hasPermission(permissions, PERMISSIONS.ACCESS_SETTINGS),
      canExposeSettings: hasPermission(permissions, PERMISSIONS.EXPOSSE_SETTINGS),
      canEmailSettings: hasPermission(permissions, PERMISSIONS.EMAIL_SETTINGS)
    };
    
    return {
      ...permissionHelpers,
      check,
    };
  }, [permissions]);
  
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Custom hook for accessing the context
export const usePermissions = () => useContext(PermissionsContext);

// Conditional wrapper component for permission-based rendering
export const Permissioned = ({ 
  children, 
  permission,
  permissions,
  requireAll = false,
  fallback = null 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  
  let hasAccess = false;
  
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }
  
  return hasAccess ? children : fallback;
};
